import qs from 'qs';
import { NavigateOptions, To, useNavigate, useSearchParams } from 'react-router-dom';

type AppNavigateFunction = (
  to: To,
  options?: NavigateOptions & { keepRedirectUrl?: boolean; goToRedirectUrlIfExists?: boolean },
) => void | Promise<void>;

const useAppNavigate = () => {
  const navigateRRD = useNavigate();
  const [searchParams] = useSearchParams();
  const searchObject = Object.fromEntries(searchParams);
  const { redirectUrl } = searchObject;

  const getIsGoToRedirectUrl = (goToRedirectUrlIfExists?: boolean) => {
    return goToRedirectUrlIfExists && redirectUrl;
  };

  const getPathname = (to: To, goToRedirectUrlIfExists?: boolean): string | undefined => {
    if (getIsGoToRedirectUrl(goToRedirectUrlIfExists)) {
      return decodeURIComponent(redirectUrl);
    }

    if (typeof to === 'string') {
      return to;
    }

    return to.pathname;
  };

  const getSearch = (to: To, keepRedirectUrl?: boolean): string | undefined => {
    const qsObj = qs.parse((typeof to === 'string' ? to : to.search) || '');

    if (keepRedirectUrl && redirectUrl) {
      return qs.stringify({ ...qsObj, redirectUrl });
    }

    return qs.stringify(qsObj);
  };

  const navigate: AppNavigateFunction = (to, appOptions) => {
    const {
      keepRedirectUrl = false,
      goToRedirectUrlIfExists = false,
      ...options
    } = appOptions || {};

    const pathname = getPathname(to, goToRedirectUrlIfExists);

    if (getIsGoToRedirectUrl(goToRedirectUrlIfExists)) {
      navigateRRD(pathname as To);
    } else {
      navigateRRD(
        {
          pathname,
          search: getSearch(to, keepRedirectUrl),
          hash: typeof to === 'string' ? undefined : to.hash,
        },
        options,
      );
    }
  };

  return navigate;
};

export default useAppNavigate;
